import React, { useMemo, useState } from 'react';
import { FiChevronRight, FiChevronDown, FiMaximize } from 'react-icons/fi';
import { getDatePartsInTimezone } from '../../../../../utils/timezone';

// Helper to format currency
const formatCurr = (val) => {
    if (val === undefined || val === null) return '-';
    return `₹${val.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

// Helper to calculate win rate
const calcWinRate = (wins, total) => {
    if (total === 0) return '0%';
    return `${((wins / total) * 100).toFixed(1)}%`;
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// Aggregation logic — uses exchange timezone for grouping so trades
// on the same exchange calendar day always appear together.
const aggregateTrades = (trades, exchangeTimezone = 'Asia/Kolkata') => {
    if (!trades || !trades.length) return [];

    const root = { sumPL: 0, wins: 0, total: 0, years: {} };

    // Use regular for loop for max performance
    for (let i = 0; i < trades.length; i++) {
        const t = trades[i];
        const exitTime = t.exitTime ?? t.exit_time;
        const entryTime = t.entryTime ?? t.entry_time;
        const pnl = Number(t.pnl ?? t.net_pnl ?? 0);
        
        if (!exitTime) continue; // Only closed trades

        // --> Extract date parts in the EXCHANGE timezone (not browser local)
        const dp = getDatePartsInTimezone(exitTime, exchangeTimezone);
        if (!dp) continue; // Protection against invalid dates
        
        const year = dp.year;
        const monthIdx = dp.month; // 0-indexed
        const month = MONTH_NAMES[monthIdx];
        // Calculate quarter
        const q = Math.floor(monthIdx / 3) + 1;
        const quarter = `Q${q}`;
        
        const dayNum = String(dp.day).padStart(2, '0');
        const dayName = DAY_NAMES[dp.weekday];
        const dayKey = `${month} ${dayNum}, ${year} ${dayName}`;
        
        const isWin = pnl > 0;

        // Init Year
        if (!root.years[year]) root.years[year] = { 
            key: year, type: 'year', sumPL: 0, wins: 0, total: 0, 
            quarters: {}, start: exitTime, end: exitTime 
        };
        const yNode = root.years[year];
        
        // Init Quarter
        if (!yNode.quarters[quarter]) yNode.quarters[quarter] = { 
            key: quarter, type: 'quarter', sumPL: 0, wins: 0, total: 0, 
            months: {}, start: exitTime, end: exitTime 
        };
        const qNode = yNode.quarters[quarter];

        // Init Month
        if (!qNode.months[month]) qNode.months[month] = { 
            key: `${month} '${String(year).slice(-2)}`, type: 'month', sumPL: 0, wins: 0, total: 0, 
            days: {}, start: exitTime, end: exitTime 
        };
        const mNode = qNode.months[month];

        // Init Day
        if (!mNode.days[dayKey]) mNode.days[dayKey] = { 
            key: dayKey, type: 'day', sumPL: 0, wins: 0, total: 0, 
            trades: [], start: exitTime, end: exitTime 
        };
        const dNode = mNode.days[dayKey];

        // Helper to update node bounds and stats
        const updateNode = (node) => {
            node.sumPL += pnl;
            node.total += 1;
            if (isWin) node.wins += 1;
            
            if (new Date(entryTime) < new Date(node.start)) node.start = entryTime;
            if (new Date(exitTime) > new Date(node.end)) node.end = exitTime;
        };

        // Update all hierarchy levels
        updateNode(yNode);
        updateNode(qNode);
        updateNode(mNode);
        updateNode(dNode);
        
        // Root update
        root.sumPL += pnl;
        root.total += 1;
        if (isWin) root.wins += 1;

        // Add trade to day
        dNode.trades.push({
            ...t,
            key: `Trade ${t.id || dNode.trades.length + 1}`,
            type: 'trade',
            pnl,
            entryTime,
            exitTime,
            entryPrice: Number(t.entryPrice ?? t.entry_price ?? 0),
        });
    }

    // Convert objects to sorted arrays
    const result = Object.values(root.years).sort((a, b) => b.key - a.key).map(y => {
        y.children = Object.values(y.quarters).sort((a,b) => a.key.localeCompare(b.key)).map(q => {
            q.children = Object.values(q.months).map(m => {
                m.children = Object.values(m.days).sort((a, b) => new Date(b.start) - new Date(a.start)).map(d => {
                    d.children = d.trades;
                    delete d.trades;
                    return d;
                });
                delete m.days;
                return m;
            });
            delete q.months;
            return q;
        });
        delete y.quarters;
        return y;
    });

    return result;
};


const PeriodRow = ({ node, depth = 0, onPeriodSelect }) => {
    const [expanded, setExpanded] = useState(depth === 0); // Exapnd years by default
    const isTrade = node.type === 'trade';
    const hasChildren = node.children && node.children.length > 0;
    
    return (
        <>
            <div 
                className={`flex items-center p-2 border-b border-[var(--divider)] hover:bg-bg-3/50 transition-colors ${depth === 0 ? 'bg-bg-3/50 dark:bg-bg-1/30' : ''} ${hasChildren ? 'cursor-pointer' : ''}`}
                style={{ paddingLeft: `${depth * 16 + 8}px` }}
                onClick={() => hasChildren && setExpanded(!expanded)}
            >
                <div className="flex items-center w-6 text-fg-3">
                    {hasChildren && (expanded ? <FiChevronDown /> : <FiChevronRight />)}
                </div>
                
                <div className="flex-1 flex items-center gap-2">
                    <span className={`text-sm ${depth === 0 ? 'font-bold text-fg-1' : isTrade ? 'text-fg-2 font-mono text-xs' : 'font-semibold text-fg-2'}`}>
                        {isTrade ? `Trade ${node.side} @ ${node.entryPrice?.toFixed(2)}` : node.key}
                    </span>
                    
                    {!isTrade && (
                        <span className="text-[10px] text-fg-3 bg-bg-3 px-1.5 py-0.5 rounded font-mono">
                            {node.total} trades
                        </span>
                    )}

                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            if(onPeriodSelect) {
                                let st = node.start;
                                let en = node.end;
                                if(isTrade) {
                                    st = node.entryTime;
                                    en = node.exitTime;
                                }
                                onPeriodSelect({ start: st, end: en });
                            }
                        }}
                        className="ml-auto p-1 text-fg-3 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                        title="View on Chart"
                    >
                        <FiMaximize size={14} />
                    </button>
                </div>
                
                <div className="w-24 text-right flex flex-col items-end">
                    <span className={`text-sm font-mono font-bold ${node.sumPL >= 0 || node.pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {formatCurr(isTrade ? node.pnl : node.sumPL)}
                    </span>
                    {!isTrade && (
                         <span className="text-[10px] text-fg-2">
                             Win: {calcWinRate(node.wins, node.total)}
                         </span>
                    )}
                </div>
            </div>
            
            {expanded && hasChildren && (
                <div className="flex flex-col">
                    {node.children.map((child, i) => (
                        <PeriodRow key={i} node={child} depth={depth + 1} onPeriodSelect={onPeriodSelect} />
                    ))}
                </div>
            )}
        </>
    );
};


const BacktestPeriodAnalysis = ({ trades, onPeriodSelect, exchangeTimezone = 'Asia/Kolkata' }) => {
    const aggregatedData = useMemo(() => aggregateTrades(trades, exchangeTimezone), [trades, exchangeTimezone]);

    if (!trades || trades.length === 0) {
        return (
            <div className="h-full flex items-center justify-center text-sm font-medium text-fg-3">
                No trades available for period analysis.
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] overflow-hidden">
            <div className="p-3 border-b border-[var(--divider)] bg-bg-3/50 dark:bg-bg-1/50 flex justify-between items-center shrink-0">
                <span className="ds-eyebrow text-fg-2">Period Analysis</span>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {aggregatedData.map(yearNode => (
                    <PeriodRow key={yearNode.key} node={yearNode} onPeriodSelect={onPeriodSelect} />
                ))}
            </div>
        </div>
    );
};

export default BacktestPeriodAnalysis;
